import React, { useEffect, useState } from 'react';
import { useLang } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import api, { saasAdminSyncByEmail, saasAdminGrantByEmail } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Search, Users, Save, XCircle, Mail, Building2, DollarSign, ArrowUpRight, TrendingUp, CheckSquare, Square, RefreshCw } from 'lucide-react';

const PLAN_STYLES = {
  free: 'bg-gray-100 text-gray-700', starter: 'bg-blue-100 text-blue-700',
  growth: 'bg-blue-100 text-blue-700', enterprise: 'bg-purple-100 text-purple-700'
};
const STATUS_STYLES = {
  active: 'text-blue-700', trialing: 'text-amber-700',
  past_due: 'text-orange-700', canceled: 'text-rose-700', none: 'text-gray-500'
};

const AdminSubscribers = () => {
  const { lang } = useLang();
  const [subs, setSubs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState('');
  const [syncEmail, setSyncEmail] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [grantMode, setGrantMode] = useState(false); // false=Stripe sync, true=Manual grant
  const [grantPlan, setGrantPlan] = useState('growth');
  const [grantMonths, setGrantMonths] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [checked, setChecked] = useState(new Set());
  const [bulkPlan, setBulkPlan] = useState('growth');

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get('/saas/admin/subscribers'),
        api.get('/saas/admin/analytics'),
      ]);
      setSubs(r.data.subscribers || []);
      setAnalytics(a.data);
    } catch { toast({ title: 'Load failed', variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setChecked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (checked.size === subs.length) setChecked(new Set());
    else setChecked(new Set(subs.map((s) => s.user_id)));
  };
  const bulkCancel = async () => {
    if (!checked.size) return;
    if (!window.confirm(lang === 'ar' ? `إلغاء اشتراك ${checked.size} مشترك؟` : `Cancel ${checked.size} subscriptions?`)) return;
    try {
      await api.post('/saas/admin/subscribers/bulk', { user_ids: [...checked], action: 'cancel' });
      toast({ title: lang === 'ar' ? 'تم الإلغاء' : 'Canceled' });
      setChecked(new Set()); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };
  const bulkUpgrade = async () => {
    if (!checked.size) return;
    try {
      await api.post('/saas/admin/subscribers/bulk', { user_ids: [...checked], action: 'upgrade', plan: bulkPlan });
      toast({ title: lang === 'ar' ? `تمت الترقية إلى ${bulkPlan}` : `Upgraded to ${bulkPlan}` });
      setChecked(new Set()); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const openDetail = async (s) => {
    setSelected(s); setDetail(null);
    try {
      const r = await api.get(`/saas/admin/subscribers/${s.user_id}`);
      setDetail(r.data);
      setForm({
        plan: r.data.subscription?.plan || 'free',
        status: r.data.subscription?.status || 'none',
        current_period_end: (r.data.subscription?.current_period_end || '').slice(0, 10),
        full_name: r.data.user?.full_name || r.data.user?.name || '',
        phone: r.data.user?.phone || '',
        company_name: (r.data.companies || [])[0] || '',
        is_admin: !!r.data.user?.is_admin,
      });
    } catch { toast({ title: 'Failed to load', variant: 'destructive' }); }
  };

  const save = async () => {
    if (!selected) return;
    try {
      const payload = { ...form };
      if (payload.current_period_end) {
        payload.current_period_end = new Date(payload.current_period_end).toISOString();
      } else { delete payload.current_period_end; }
      await api.patch(`/saas/admin/subscribers/${selected.user_id}`, payload);
      toast({ title: lang === 'ar' ? 'حُفظت التغييرات' : 'Saved' });
      setSelected(null); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const cancelSub = async () => {
    if (!selected) return;
    if (!window.confirm(lang === 'ar' ? 'إلغاء اشتراك هذا المشترك؟' : 'Cancel this subscription?')) return;
    try {
      await api.delete(`/saas/admin/subscribers/${selected.user_id}/subscription`);
      toast({ title: lang === 'ar' ? 'أُلغي الاشتراك' : 'Subscription canceled' });
      setSelected(null); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const filtered = subs.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.user?.email || '').toLowerCase().includes(q)
      || (s.user?.full_name || s.user?.name || '').toLowerCase().includes(q)
      || (s.plan || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="admin-subscribers">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'مشتركو AI Accounting' : 'AI Accounting Subscribers'}</h1>
          <p className="text-sm text-[#111827]/60 mt-2">{subs.length} {lang === 'ar' ? 'مشترك' : 'subscribers'}</p>
        </div>
      </div>

      {/* Analytics strip */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label={lang === 'ar' ? 'MRR الحالي' : 'Current MRR'} value={`${(analytics.current_mrr || 0).toLocaleString()} AED`} accent="emerald" />
          <Stat label={lang === 'ar' ? 'المشتركون النشطون' : 'Active'} value={analytics.active_subscribers || 0} />
          <Stat label={lang === 'ar' ? 'إجمالي المفتوحة' : 'Open (active+trial)'} value={analytics.total_open || 0} />
          <Stat label={lang === 'ar' ? 'معدل الفقد' : 'Churn'} value={`${analytics.churn_rate || 0}%`} accent="rose" />
        </div>
      )}

      {/* Sparkline chart */}
      {analytics?.by_month?.length > 0 && (
        <div className="glass-card rounded-2xl p-5" data-testid="mrr-chart">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#2563eb]" />
            <div className="text-xs uppercase tracking-widest text-[#2563eb] font-bold">{lang === 'ar' ? 'الإيرادات الشهرية (MRR)' : 'Monthly Recurring Revenue'}</div>
          </div>
          <div className="flex items-end gap-2 h-32">
            {analytics.by_month.map((m) => {
              const max = Math.max(1, ...analytics.by_month.map((x) => x.mrr));
              const h = Math.max(6, (m.mrr / max) * 100);
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[10px] text-[#2563eb] font-mono tabular-nums">{m.mrr > 0 ? Math.round(m.mrr).toLocaleString() : ''}</div>
                  <div className="w-full bg-gradient-to-t from-[#2563eb] to-[#3b82f6] rounded-t-md" style={{ height: `${h}%` }} title={`${m.month}: ${m.mrr} AED`} />
                  <div className="text-[9px] text-[#111827]/50">{m.month.slice(-2)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bulk toolbar */}
      {checked.size > 0 && (
        <div className="glass-card rounded-xl p-3 flex items-center gap-3 bg-blue-50 border-blue-200" data-testid="bulk-toolbar">
          <div className="text-xs text-[#2563eb] font-semibold">{checked.size} {lang === 'ar' ? 'محدَّد' : 'selected'}</div>
          <Select value={bulkPlan} onValueChange={setBulkPlan}>
            <SelectTrigger className="w-40 h-9 bg-white border-[#e5e7eb] text-xs" data-testid="bulk-plan"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="growth">Growth</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={bulkUpgrade} className="gold-button rounded-md h-9 px-3 text-xs" data-testid="bulk-upgrade">{lang === 'ar' ? 'ترقية الكل' : 'Upgrade All'}</Button>
          <Button onClick={bulkCancel} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent border-rose-400 text-rose-500" data-testid="bulk-cancel"><XCircle className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'إلغاء الكل' : 'Cancel All'}</Button>
          <Button onClick={() => setChecked(new Set())} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent ms-auto">{lang === 'ar' ? 'مسح التحديد' : 'Clear'}</Button>
        </div>
      )}

      <div className="glass-card rounded-xl p-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-[#2563eb]/70 ms-2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={lang === 'ar' ? 'بحث بالبريد، الاسم، الباقة' : 'Search by email, name, plan'} className="border-0 bg-transparent focus-visible:ring-0" />
      </div>

      <div className="glass-card rounded-xl p-3 border-2 border-dashed border-amber-300 bg-amber-50/40 space-y-2" data-testid="sync-by-email-card">
        <div className="flex items-center gap-2 flex-wrap">
          <RefreshCw className="w-4 h-4 text-amber-600 ms-2" />
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold">
            <button
              onClick={() => setGrantMode(false)}
              className={`px-2 py-1 rounded ${!grantMode ? 'bg-amber-500 text-white' : 'bg-white text-amber-900 border border-amber-300'}`}
              data-testid="mode-sync"
            >
              {lang === 'ar' ? 'Stripe' : 'Sync Stripe'}
            </button>
            <button
              onClick={() => setGrantMode(true)}
              className={`px-2 py-1 rounded ${grantMode ? 'bg-blue-600 text-white' : 'bg-white text-blue-900 border border-blue-300'}`}
              data-testid="mode-grant"
            >
              {lang === 'ar' ? 'منح يدوي' : 'Manual Grant'}
            </button>
          </div>
          <Input
            value={syncEmail}
            onChange={(e) => setSyncEmail(e.target.value)}
            placeholder={grantMode
              ? (lang === 'ar' ? 'بريد الزبون لمنح اشتراك مجاني' : 'Customer email to grant free access')
              : (lang === 'ar' ? 'بريد الزبون الذي دفع على Stripe' : 'Email of customer who paid on Stripe')}
            className="border-0 bg-transparent focus-visible:ring-0 flex-1 min-w-[220px]"
            data-testid="sync-email-input"
          />
          {grantMode && (
            <>
              <Select value={grantPlan} onValueChange={setGrantPlan}>
                <SelectTrigger className="w-32 h-9 bg-white border-blue-300" data-testid="grant-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="growth">Growth</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(grantMonths)} onValueChange={(v) => setGrantMonths(Number(v))}>
                <SelectTrigger className="w-28 h-9 bg-white border-blue-300" data-testid="grant-months"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 3, 6, 12, 24, 36].map((m) => (
                    <SelectItem key={m} value={String(m)}>{m} {lang === 'ar' ? (m === 1 ? 'شهر' : 'أشهر') : (m === 1 ? 'month' : 'months')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <Button
            disabled={syncing || !syncEmail.trim()}
            onClick={async () => {
              setSyncing(true);
              try {
                let r;
                if (grantMode) {
                  r = await saasAdminGrantByEmail({ email: syncEmail.trim().toLowerCase(), plan: grantPlan, months: grantMonths, status: 'active' });
                  toast({ title: lang === 'ar' ? `✅ مُنح ${r.plan?.toUpperCase()} · ${r.months} شهر` : `✅ Granted ${r.plan?.toUpperCase()} · ${r.months} month${r.months === 1 ? '' : 's'}` });
                } else {
                  r = await saasAdminSyncByEmail(syncEmail.trim().toLowerCase());
                  toast({ title: lang === 'ar' ? `✅ فُعِّل ${r.plan?.toUpperCase()}` : `✅ Activated ${r.plan?.toUpperCase()}` });
                }
                setSyncEmail('');
                await load();
              } catch (e) {
                toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
              } finally { setSyncing(false); }
            }}
            className={`${grantMode ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white' : 'gold-button'} rounded-md h-9 px-4 text-xs`}
            data-testid="sync-email-btn"
          >
            {syncing ? '…' : grantMode
              ? (lang === 'ar' ? 'منح الآن' : 'Grant now')
              : (lang === 'ar' ? 'تفعيل من Stripe' : 'Sync from Stripe')}
          </Button>
        </div>
        <div className="text-[10px] text-amber-900/60 ms-6">
          {grantMode
            ? (lang === 'ar' ? '⚡ منح اشتراك مجاناً بدون Stripe — مثالي للـ VIP، الاختبار، أو الحلول التعويضية' : '⚡ Grant a free subscription without Stripe — for VIPs, testing, or comps')
            : (lang === 'ar' ? '🔄 يبحث النظام في Stripe عن دفعة ناجحة لهذا البريد ويُفعّل الاشتراك تلقائياً' : '🔄 Looks up Stripe for a paid subscription tied to that email and activates it')}
        </div>
      </div>

      <div className="glass-card rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#f8fafc]">
            <tr>
              <th className="p-3 w-8">
                <button onClick={toggleAll} className="text-[#2563eb]" data-testid="check-all">
                  {checked.size === subs.length && subs.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>
              </th>
              <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'المشترك' : 'Subscriber'}</th>
              <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الباقة' : 'Plan'}</th>
              <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الحالة' : 'Status'}</th>
              <th className="p-3 text-start text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الشركات' : 'Companies'}</th>
              <th className="p-3 text-end text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الإيرادات' : 'Revenue'}</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan="7" className="p-10 text-center text-[#111827]/40">…</td></tr>) :
              filtered.length === 0 ? (<tr><td colSpan="7" className="p-10 text-center text-xs text-[#111827]/40">{lang === 'ar' ? 'لا مشتركين' : 'No subscribers yet'}</td></tr>) :
                filtered.map((s) => (
                  <tr key={s.user_id} className="border-t border-[#e5e7eb] hover:bg-[#f8fafc]" data-testid={`sub-row-${s.user_id}`}>
                    <td className="p-3">
                      <button onClick={() => toggle(s.user_id)} className="text-[#2563eb]" data-testid={`check-${s.user_id}`}>
                        {checked.has(s.user_id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-[#111827]/40" />}
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="text-[#0a0a0a] font-medium">{s.user?.full_name || s.user?.name || '—'}</div>
                      <div className="text-[10px] text-[#111827]/50 flex items-center gap-1"><Mail className="w-2.5 h-2.5" /> {s.user?.email}</div>
                    </td>
                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded font-bold ${PLAN_STYLES[s.plan] || PLAN_STYLES.free}`}>{s.plan?.toUpperCase() || 'FREE'}</span></td>
                    <td className={`p-3 text-xs capitalize ${STATUS_STYLES[s.status] || STATUS_STYLES.none}`}>{s.status}</td>
                    <td className="p-3 text-[#111827]/70 text-xs">
                      {(s.companies || []).length ? (
                        <div className="flex flex-wrap gap-1">
                          {s.companies.slice(0, 2).map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200"><Building2 className="w-2.5 h-2.5 inline me-0.5" />{c}</span>)}
                          {s.companies.length > 2 && <span className="text-[10px] text-[#111827]/40">+{s.companies.length - 2}</span>}
                        </div>
                      ) : <span className="text-[#111827]/30">—</span>}
                    </td>
                    <td className="p-3 text-end text-[#2563eb] font-mono">{(s.total_revenue || 0).toLocaleString()} AED</td>
                    <td className="p-3 text-end">
                      <Button onClick={() => openDetail(s)} className="ghost-button rounded-md h-8 px-3 text-[11px] bg-transparent" data-testid={`edit-${s.user_id}`}>
                        {lang === 'ar' ? 'تعديل' : 'Edit'} <ArrowUpRight className="w-3 h-3 ms-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Detail / Edit Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="bg-white border-[#e5e7eb] text-[#0a0a0a] max-w-3xl max-h-[92vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0a0a0a] font-display text-2xl">
                  {selected.user?.full_name || selected.user?.name} · <span className="text-[#2563eb] text-base font-mono">{selected.user?.email}</span>
                </DialogTitle>
              </DialogHeader>

              {!detail ? <div className="py-10 text-center text-[#111827]/40 text-xs">…</div> : (
                <div className="space-y-5">
                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-3">
                    <Stat label={lang === 'ar' ? 'المعاملات' : 'Transactions'} value={detail.txn_count} />
                    <Stat label={lang === 'ar' ? 'الشركات' : 'Companies'} value={(detail.companies || []).length} />
                    <Stat label={lang === 'ar' ? 'الإيصالات' : 'Receipts'} value={detail.receipts_count} />
                    <Stat label={lang === 'ar' ? 'الإيرادات' : 'Revenue'} value={`${(selected.total_revenue || 0).toLocaleString()} AED`} />
                  </div>

                  {/* Subscription */}
                  <div className="glass-card rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#2563eb] mb-3">{lang === 'ar' ? 'الاشتراك' : 'Subscription'}</div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label className="text-xs">{lang === 'ar' ? 'الباقة' : 'Plan'}</Label>
                        <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                          <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e5e7eb]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">Starter (199 AED)</SelectItem>
                            <SelectItem value="growth">Growth (499 AED)</SelectItem>
                            <SelectItem value="enterprise">Enterprise (1499 AED)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">{lang === 'ar' ? 'الحالة' : 'Status'}</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                          <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e5e7eb]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">active</SelectItem>
                            <SelectItem value="trialing">trialing</SelectItem>
                            <SelectItem value="past_due">past_due</SelectItem>
                            <SelectItem value="canceled">canceled</SelectItem>
                            <SelectItem value="none">none</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">{lang === 'ar' ? 'ينتهي في' : 'Renews / Expires'}</Label>
                        <Input type="date" value={form.current_period_end || ''} onChange={(e) => setForm({ ...form, current_period_end: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" />
                      </div>
                    </div>
                  </div>

                  {/* User details */}
                  <div className="glass-card rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#2563eb] mb-3">{lang === 'ar' ? 'معلومات المشترك والشركة' : 'Subscriber & Company Info'}</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">{lang === 'ar' ? 'الاسم' : 'Full name'}</Label>
                        <Input value={form.full_name || ''} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" />
                      </div>
                      <div><Label className="text-xs">{lang === 'ar' ? 'الهاتف' : 'Phone'}</Label>
                        <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" />
                      </div>
                      <div><Label className="text-xs">{lang === 'ar' ? 'اسم الشركة (يُعدَّل على كل المعاملات)' : 'Company name (rewrites all txns)'}</Label>
                        <Input value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" />
                      </div>
                      <div className="flex items-end gap-2">
                        <label className="flex items-center gap-2 text-xs text-[#111827]/80 cursor-pointer mb-2">
                          <input type="checkbox" checked={!!form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} className="w-4 h-4 accent-[#2563eb]" />
                          {lang === 'ar' ? 'مدير' : 'Admin privileges'}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Recent transactions */}
                  {detail.recent_transactions?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#2563eb] mb-2">{lang === 'ar' ? 'آخر المعاملات' : 'Recent Transactions'}</div>
                      <div className="max-h-40 overflow-y-auto border border-[#e5e7eb] rounded-lg">
                        <table className="w-full text-xs">
                          <tbody>
                            {detail.recent_transactions.map((t) => (
                              <tr key={t.id} className="border-b border-[#e5e7eb] last:border-0">
                                <td className="p-2 text-[#111827]/60 whitespace-nowrap">{t.date}</td>
                                <td className="p-2 text-[#0a0a0a] truncate max-w-xs">{t.merchant || t.description}</td>
                                <td className="p-2"><span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 uppercase">{t.category}</span></td>
                                <td className={`p-2 text-end font-mono ${t.amount >= 0 ? 'text-[#2563eb]' : 'text-rose-600'}`}>{Number(t.amount).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button onClick={cancelSub} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent border-rose-400 text-rose-500" data-testid="cancel-sub">
                  <XCircle className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'إلغاء الاشتراك' : 'Cancel Subscription'}
                </Button>
                <Button onClick={save} className="gold-button rounded-md h-10 px-5 text-xs" data-testid="save-sub"><Save className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'حفظ' : 'Save'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="rounded-lg border border-[#e5e7eb] bg-white p-3">
    <div className="text-[10px] uppercase tracking-widest text-[#111827]/50">{label}</div>
    <div className={`text-lg font-bold mt-1 ${accent === 'emerald' ? 'text-[#2563eb]' : accent === 'rose' ? 'text-rose-600' : 'text-[#0a0a0a]'}`}>{value}</div>
  </div>
);

export default AdminSubscribers;
